import { useEffect, useMemo, useState } from 'react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { TextInput } from '../../../shared/components/TextInput';
import { useEstados, useMunicipios } from '../../geo/hooks';
import { Holiday, HolidayScope, HolidayType, mensagemDeErro } from '../api';
import { useSalvarFeriado } from '../hooks';
import { CadastroModal } from './CadastroModal';
import { SCOPE_LABEL, TYPE_LABEL } from './format';

interface Props {
  open: boolean;
  feriado: Holiday | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}

export function FeriadoFormModal({ open, feriado, onClose, onSaved }: Props) {
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<HolidayType>('feriado');
  const [scope, setScope] = useState<HolidayScope>('municipal');
  const [uf, setUf] = useState('');
  const [cityId, setCityId] = useState('');
  const [recurring, setRecurring] = useState(false);
  const salvar = useSalvarFeriado();
  const estados = useEstados();
  const municipios = useMunicipios(scope === 'municipal' ? uf : null);

  useEffect(() => {
    if (!open) return;
    setDate(feriado?.date ?? '');
    setName(feriado?.name ?? '');
    setType(feriado?.type ?? 'feriado');
    setScope(feriado?.scope ?? 'municipal');
    setUf(feriado?.stateUf ?? '');
    setCityId(feriado?.cityId ? String(feriado.cityId) : '');
    setRecurring(feriado?.recurring ?? false);
    salvar.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, feriado]);

  const ufOptions = useMemo(
    () => (estados.data ?? []).map((e) => ({ value: e.uf, label: `${e.nome} (${e.uf})` })),
    [estados.data],
  );
  const cityOptions = useMemo(
    () => (municipios.data ?? []).map((m) => ({ value: String(m.idIbge), label: m.nome })),
    [municipios.data],
  );

  const locationOk =
    scope === 'nacional' || (scope === 'estadual' && uf !== '') || (scope === 'municipal' && cityId !== '');

  function changeScope(next: HolidayScope) {
    setScope(next);
    if (next === 'nacional') {
      setUf('');
      setRecurring(false);
    }
    setCityId('');
  }

  async function handleSubmit() {
    try {
      await salvar.mutateAsync({
        id: feriado?.id,
        payload: {
          date,
          name: name.trim(),
          type,
          scope,
          stateUf: scope === 'estadual' ? uf : null,
          cityId: scope === 'municipal' ? Number(cityId) : null,
          recurring: scope === 'nacional' ? false : recurring,
        },
      });
      onSaved(feriado ? 'Feriado atualizado.' : 'Feriado cadastrado.');
    } catch {
      // Erro exibido no próprio modal.
    }
  }

  return (
    <CadastroModal
      open={open}
      title={feriado ? 'Editar feriado' : 'Novo feriado'}
      description="Feriados nacionais são criados pela ação “Gerar feriados do ano”."
      submitLabel="Salvar"
      isSubmitting={salvar.isPending}
      submitError={salvar.isError ? mensagemDeErro(salvar.error, 'Não foi possível salvar o feriado.') : null}
      canSubmit={date !== '' && name.trim() !== '' && locationOk}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <TextInput label="Data" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      <TextInput label="Nome" value={name} maxLength={160} onChange={(e) => setName(e.target.value)} />
      <AutocompleteSelect
        label="Tipo"
        options={(Object.keys(TYPE_LABEL) as HolidayType[]).map((t) => ({ value: t, label: TYPE_LABEL[t] }))}
        value={type}
        onChange={(v) => setType(v as HolidayType)}
      />
      <AutocompleteSelect
        label="Abrangência"
        options={(Object.keys(SCOPE_LABEL) as HolidayScope[]).map((s) => ({ value: s, label: SCOPE_LABEL[s] }))}
        value={scope}
        onChange={(v) => changeScope(v as HolidayScope)}
      />
      {scope !== 'nacional' && (
        <AutocompleteSelect
          label="Estado"
          options={ufOptions}
          value={uf}
          onChange={(v) => {
            setUf(v);
            setCityId('');
          }}
        />
      )}
      {scope === 'municipal' && (
        <AutocompleteSelect
          label="Município"
          options={cityOptions}
          value={cityId}
          onChange={setCityId}
          disabled={!uf || municipios.isLoading}
        />
      )}
      {scope !== 'nacional' && (
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300"
            checked={recurring}
            onChange={(e) => setRecurring(e.target.checked)}
          />
          Repetir todo ano na mesma data
        </label>
      )}
    </CadastroModal>
  );
}
